import { StyledTextField } from '@resurs-internal/mui';
import { StyledTextFieldProps } from '@resurs-internal/mui/components/StyledTextField/StyledTextField';
import { useLayoutEffect, useRef, useState } from 'react';
import { ChangeEvent } from 'react';

import { useAppSelector } from '../../hooks';
import useFieldFormatting, { FieldFormattingProps } from '../../hooks/useFieldFormatting';
import { FieldValidationProps } from '../../hooks/useFieldValidation';
import useTranslation from '../../hooks/useTranslation';
import { selectFieldStateByName } from '../../store/applicationform/applicationFormSelectors';
import InputField from '../InputField/InputField';

export type TextInputFieldProps = {
    /**
     * The name of the input field which will be used to for storing the value in the store
     */
    name: string;

    /**
     * An optional label which will also be displayed as a placeholder
     */
    label?: string;

    /**
     * When the text is changed this function will be invoked after the value has been updated in the store.
     */
    onChange?: (event: ChangeEvent) => void;

    /**
     * Validation properties
     */
    validation?: FieldValidationProps;

    /**
     * Formatting properties
     */
    formatting?: FieldFormattingProps;

    disabled?: boolean;
    /**
     * An optional min and max value for numbers
     */
    min?: number;
    max?: number;
} & StyledTextFieldProps;

const TextInputField = (props: TextInputFieldProps) => {
    const translate = useTranslation();
    const position = useRef({
        beforeStart: 0,
        beforeEnd: 0,
    });

    const [isEditing, setIsEditing] = useState(false);
    const { formatValue, cleanValue } = useFieldFormatting(props.name, props.formatting);
    const fieldState = useAppSelector(selectFieldStateByName)(props.name);
    const inputElement: HTMLElement = document.getElementsByName(props.name)[0];

    useLayoutEffect(() => {
        if (
            inputElement instanceof HTMLInputElement &&
            inputElement.type !== 'email' &&
            inputElement.type !== 'number'
        ) {
            inputElement.setSelectionRange(position.current.beforeStart, position.current.beforeEnd);
        }
    }, [fieldState]);

    const setPosition = (event: any): void => {
        const cleanedValue = cleanValue(event.target.value);
        const formattedValue = formatValue(cleanedValue);
        const initLength = event.target.value.length;
        const delta = (formattedValue?.length ?? 0) - initLength;

        position.current = {
            beforeStart: event.target.selectionStart + delta,
            beforeEnd: event.target.selectionEnd + delta,
        };
    };

    return (
        <>
            <InputField postOnChange={setPosition} {...props}>
                <StyledTextField
                    onBlur={() => setIsEditing(false)}
                    onFocus={() => setIsEditing(true)}
                    error={fieldState?.hasError && !isEditing}
                    helperText={fieldState?.hasError && !isEditing ? translate(fieldState?.errorMessage ?? '') : ''}
                    {...props}
                    sx={(theme) => ({
                        overflow: 'hidden',
                        ...props.sx,
                        borderColor: `${theme.palette.grey[400]} !important`,
                    })}
                    InputLabelProps={{
                        'aria-hidden': true,
                    }}
                    aria-label={props.label}
                    aria-live="assertive"
                />
            </InputField>
        </>
    );
};

export default TextInputField;
